import { Controller } from "@hotwired/stimulus"

// Longest side of the PNG an SVG becomes.
const SIDE = 800
// Used when an SVG declares neither a size nor a viewBox.
const FALLBACK_SIZE = 300

// Turns a picked SVG into a PNG before the form submits, so the server only ever
// stores raster images: Active Storage can't make a variant out of an SVG (Rails
// blocks libvips' SVG loader), and serving a user's SVG would mean running its
// scripts. An <img> never runs them, so drawing the SVG on a canvas is safe.
// Any other file passes through untouched. Goes on the <input type=file> itself.
export default class extends Controller {
  async convert() {
    // Every pick starts clean: a failed SVG must not keep blocking the next valid file.
    this.element.setCustomValidity("")
    const file = this.element.files[0]
    if (!file || !this.#svg(file)) return

    // Native validation holds the submit until the PNG is in: sent early, the raw SVG
    // would be refused as "formato não suportado" — a file the field itself offered.
    this.element.setCustomValidity("Convertendo o SVG…")
    try {
      this.#replaceWith(await this.#rasterize(file))
      this.element.setCustomValidity("")
    } catch {
      this.element.value = ""
      this.element.setCustomValidity("Não foi possível ler este SVG.")
      this.element.reportValidity()
    }
  }

  // The extension too: some systems hand a .svg over with an empty type.
  #svg(file) {
    return file.type === "image/svg+xml" || /\.svg$/i.test(file.name)
  }

  async #rasterize(file) {
    const image = await this.#load(await file.text())
    const ratio = Math.min(SIDE / image.naturalWidth, SIDE / image.naturalHeight)
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(image.naturalWidth * ratio)
    canvas.height = Math.round(image.naturalHeight * ratio)
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"))
    if (!blob) throw new Error("canvas sem PNG")
    return new File([blob], file.name.replace(/\.svg$/i, ".png"), { type: "image/png" })
  }

  // A data: URL, not a blob: one — the CSP's img-src allows data: but not blob:.
  #load(markup) {
    const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(this.#withSize(markup))}`
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = source
    })
  }

  // An <img> sizes an SVG from its width/height, and a viewBox-only one (most exported
  // icons) has none: Firefox reports 0×0 and draws nothing. Give it the viewBox's size.
  #withSize(markup) {
    const parsed = new DOMParser().parseFromString(markup, "image/svg+xml")
    const root = parsed.documentElement
    if (parsed.querySelector("parsererror") || root.localName !== "svg") throw new Error("SVG inválido")

    const [, , viewWidth, viewHeight] = (root.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number)
    if (!this.#absolute(root.getAttribute("width"))) root.setAttribute("width", viewWidth > 0 ? viewWidth : FALLBACK_SIZE)
    if (!this.#absolute(root.getAttribute("height"))) root.setAttribute("height", viewHeight > 0 ? viewHeight : FALLBACK_SIZE)
    return new XMLSerializer().serializeToString(root)
  }

  // "100%" is relative to a container that does not exist here.
  #absolute(length) {
    return length && !length.trim().endsWith("%") && parseFloat(length) > 0
  }

  #replaceWith(file) {
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.element.files = transfer.files
  }
}
